import {
  BoxGeometry,
  ConeGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-01 Gallinazo (GDD §8.2, issue #71): a black vulture in low-poly primitives with flat shading. The
 * hitbox is a circle of 0.4 u, the 70-80 % of the model (GDD §8.1), so seen from the side (x-y) the model
 * spans about 1.04 u from beak to tail: a radius of 0.52 u. The wings beat with render time only (visual).
 */
export interface Gallinazo {
  group: Group;
  wings: [Mesh, Mesh];
  dispose(): void;
}

export const GALLINAZO_COLORS = { body: 0x1b1d22, head: 0x9a4b3c, beak: 0xc9b089 } as const;

export function buildGallinazo(): Gallinazo {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  const group = new Group();
  const body = new Mesh(own(new IcosahedronGeometry(0.22, 0)), paint(GALLINAZO_COLORS.body));
  body.scale.set(1.3, 0.8, 0.8);
  const head = new Mesh(own(new IcosahedronGeometry(0.1, 0)), paint(GALLINAZO_COLORS.head));
  head.position.set(-0.3, 0.06, 0);
  const beak = new Mesh(own(new ConeGeometry(0.04, 0.12, 4)), paint(GALLINAZO_COLORS.beak));
  beak.rotation.z = Math.PI / 2;
  beak.position.set(-0.43, 0.04, 0);
  const tail = new Mesh(own(new ConeGeometry(0.08, 0.3, 4)), paint(GALLINAZO_COLORS.body));
  tail.rotation.z = -Math.PI / 2;
  tail.position.set(0.4, 0, 0);
  const wingGeometry = own(new BoxGeometry(0.34, 0.04, 0.42));
  const wingMaterial = paint(GALLINAZO_COLORS.body);
  const left = new Mesh(wingGeometry, wingMaterial);
  left.position.set(0.02, 0.05, 0.3);
  const right = new Mesh(wingGeometry, wingMaterial);
  right.position.set(0.02, 0.05, -0.3);
  group.add(body, head, beak, tail, left, right);

  return {
    group,
    wings: [left, right],
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
